/* eslint-disable-next-line import/named */
import classNames from 'classnames';
import React, { FocusEventHandler, HTMLAttributes, ReactNode, Ref, useId } from 'react';

import { TextAreaInputEventDetail } from '@ui5/webcomponents/dist/TextArea';
import { TextAreaDomRef, Ui5CustomEvent } from '@ui5/webcomponents-react';

import { HelperText } from '../HelperText';
import { Label } from '../Label';
import { TextArea } from '../TextArea';
import styles from './FormTextArea.module.css';

export type Status = 'default' | 'error' | 'alert' | 'success' | 'info';

export interface FormTextAreaProps extends Pick<
  HTMLAttributes<HTMLDivElement>,
  'className' | 'style'
> {
  /**
   * The initial string value shown in the input field (for uncontrolled state management).
   */
  defaultValue?: string;
  /**
   * Assistive description text to be shown for the input field.
   */
  description?: ReactNode | undefined | null;
  /**
   * Whether or not the input field is disabled.
   */
  disabled?: boolean;
  /**
   * Icon to be shown in the input field.
   */
  icon?: string;
  /**
   * The ID for the input element. It is added to the input element
   * and not to the container. Should be used for accessibility linking.
   */
  id?: string;
  /**
   * The unique ID to be provided for the input DOM element.
   * It will also be used for accessibility linking.
   * If not provided, one is generated.
   */
  inputId?: string | undefined | null;
  /**
   * The label text to be shown for the input.
   */
  label: string;
  /**
   * Whether to visually hide the label, except for accessibility (e.g. screen readers)
   */
  labelHidden?: boolean;
  /**
   * Maximum allowed length of the input with HTML5 validation.
   */
  maxLength?: number;
  /**
   * Contains any dynamic message shown below the text field.
   * It's style depends on the `status` prop.
   */
  message?: ReactNode;
  /**
   * Minimum allowed length of the input with HTML5 validation.
   */
  minLength?: number;
  /**
   * A name of the form input element.
   */
  name?: string;

  /**
   * The event handler when the input field looses focus.
   */
  onBlur?: FocusEventHandler<HTMLTextAreaElement>;
  /**
   * The event handler when the value on the input field changes.
   * NOTE: If you are only interested in the new value, you can use `onValueChange` instead!
   */
  onChange?: (event: Ui5CustomEvent<TextAreaDomRef>) => void;
  /**
   * The event handler when the input field is focused.
   */
  onFocus?: FocusEventHandler<HTMLTextAreaElement>;
  /**
   * The event handler when the value on the input field changes.
   * This one takes the new value as the first parameter and the event as the second parameter.
   * It's easier to use than `onChange`.
   */
  onValueChange?: (
    value: string,
    event: Ui5CustomEvent<TextAreaDomRef, TextAreaInputEventDetail>,
  ) => void;
  /**
   * Required regexp pattern for valid values with HTML5 validation.
   */
  pattern?: string;
  /**
   * Placeholder text to show when the value is empty.
   */
  placeholder?: string;
  /**
   * Whether or not the input field is read only and cannot be edited.
   */
  readOnly?: boolean;
  /**
   * Whether or not to make the input required with HTML5 validation.
   */
  required?: boolean;
  /**
   * Defines the number of visible text lines for the component.
   *
   * **Notes:**
   *
   * *   If the `growing` property is enabled, this property defines the minimum rows to be displayed in the textarea.
   * *   The CSS `height` property wins over the `rows` property, if both are set.
   */
  rows?: number;
  /**
   * Whether or not to show the colon in the label text (without making it part of screen readers)
   */
  showColon?: boolean;
  /**
   * The current status of the field.
   */
  status?: Status;
  /**
   * Sets tooltips text shown in the label
   */
  tooltipText?: string;
  /**
   * String value shown in the input field (for controlled state management).
   */
  value?: string;
}

export const FormTextArea = React.forwardRef(
  (props: FormTextAreaProps, ref: Ref<TextAreaDomRef>) => {
    const {
      label,
      tooltipText,
      required,
      message,
      status = 'default',
      disabled = false,
      className,
      style,
      inputId: propInputId,
      labelHidden,
      onChange,
      onValueChange,
      ...textAreaProps
    } = props;
    const generatedId = useId();
    const inputId = propInputId ?? generatedId;
    const invalid = status === 'error';

    return (
      <div
        className={classNames(styles.container, className)}
        style={style}
      >
        {!labelHidden && (
          <Label
            disabled={disabled}
            for={inputId}
            required={required}
            tooltipText={tooltipText}
          >
            {label}
          </Label>
        )}
        <TextArea
          aria-invalid={invalid}
          id={inputId}
          onChange={onChange}
          onInput={
            onValueChange
              ? (event) => {
                  const newValue = event.target.value ?? '';
                  onValueChange(newValue, event);
                }
              : undefined
          }
          ref={ref}
          {...textAreaProps}
        />
        {message && (
          <HelperText
            role={status === 'error' ? 'alert' : undefined}
            showIcon
            variant={status === 'default' ? 'assistive' : status}
          >
            {message}
          </HelperText>
        )}
      </div>
    );
  },
);
